import moment from 'moment';
import { TableHeader } from '../../component/table/table.type';
import ActionButton from '../../component/button/button';
import {
  InterventionReportsKebabOption,
  InterventionReportsTableHeader,
} from './interventionReports.type';
import { OptionWithLoading } from '../userManagement/userManagement.type';
import {
  InterventionDecision,
  InterventionReport,
  ReviewStatus,
  UserType,
  GetUsersResponse,
} from '@inplay/models';
import { Dialog } from '../../component/dialog/dialog';
import Close from '../../assets/close.png';
import {
  getTranslatedTextValues,
  renderHarmObservedDialogContent,
} from '../harmReports/harmReportesHelper';
import { camelCase } from 'lodash';
import Loading from '../../assets/loading/loading';
import { Trans } from 'react-i18next';

export const getInterventionReportsTableHeader = (
  isTablet: boolean,
  t: (key: string) => string,
) => {
  const headers: TableHeader<InterventionReport>[] = [
    {
      generateCellContent: (interventionReport: InterventionReport) =>
        interventionReport?.reportRefNo ?? 0,
      header: isTablet
        ? InterventionReportsTableHeader.NO
        : InterventionReportsTableHeader.REPORT_NO,
      sortable: true,
    },
    {
      generateCellContent: (interventionReport: InterventionReport) =>
        interventionReport.createdAt
          ? moment(interventionReport.createdAt).format('hh:mm A, DD/MM/YYYY')
          : '-',
      header: InterventionReportsTableHeader.CREATED_ON,
    },
    {
      generateCellContent: (interventionReport: InterventionReport) => (
        <div className="flex items-center gap-2">
          {interventionReport.playerProfile?.faceImage && (
            <img
              src={interventionReport.playerProfile.faceImage}
              alt=""
              className="w-[40px] h-[40px] rounded-full object-cover"
            />
          )}
          <span>{interventionReport.playerProfile.id.slice(0, 5)}</span>
        </div>
      ),
      header: InterventionReportsTableHeader.PLAYER_NO,
    },
    {
      generateCellContent: (interventionReport: InterventionReport) => {
        const { lengthOfPlay, money, behaviour, other } =
          interventionReport.signsOfGamblingHarm;

        const translatedLengthOfPlay = getTranslatedTextValues(lengthOfPlay, t);
        const translatedMoney = getTranslatedTextValues(money, t);
        const translatedBehaviour = getTranslatedTextValues(behaviour, t);
        const translatedOther = getTranslatedTextValues(other, t);

        const firstItem: string =
          [
            ...translatedLengthOfPlay,
            ...translatedMoney,
            ...translatedBehaviour,
            ...translatedOther,
          ][0] || '';
        const totalCount: number =
          lengthOfPlay.length + money.length + behaviour.length + other.length;
        const maxLength: number = 20;
        const displayText =
          firstItem.length > maxLength
            ? firstItem.slice(0, maxLength) + '…'
            : firstItem;
        return (
          <div className="relative group inline-block">
            <span className="underline cursor-pointer">
              {displayText} ({totalCount})
            </span>
            {renderHarmObservedDialogContent({
              lengthOfPlay: translatedLengthOfPlay,
              money: translatedMoney,
              behaviour: translatedBehaviour,
              other: translatedOther,
            })}
          </div>
        );
      },
      header: InterventionReportsTableHeader.HARM_OBSERVED,
    },
    {
      generateCellContent: (interventionReport: InterventionReport) => {
        const translatedFollowUpActions = getTranslatedTextValues(
          interventionReport.followUpAction ?? [],
          t,
        );

        const firstItem: string = translatedFollowUpActions[0] || '';
        const totalCount: number = translatedFollowUpActions.length;
        const maxLength: number = 20;
        const displayText =
          firstItem.length > maxLength
            ? firstItem.slice(0, maxLength) + '…'
            : firstItem;
        return (
          <div className="relative group inline-block">
            <span className="underline cursor-pointer">
              {displayText} ({totalCount})
            </span>
            {renderFollowUpActionDialogContent(translatedFollowUpActions)}
          </div>
        );
      },
      header: InterventionReportsTableHeader.FOLLOW_UP_ACTIONS,
    },
    {
      generateCellContent: (interventionReport: InterventionReport) =>
        interventionReport.interventionCountForPlayer,
      header: InterventionReportsTableHeader.TOTAL_INTERVENTIONS,
    },
    {
      generateCellContent: (interventionReport: InterventionReport) => (
        <>
          <span
            className={`w-2 h-2 mr-2 rounded-full inline-block ${getInterventionReportRevieweStatusDotColor(
              interventionReport.reviewStatus,
            )}`}
          />
          {interventionReport.reviewStatus}
        </>
      ),
      header: InterventionReportsTableHeader.STATUS,
    },
    {
      generateCellContent: (interventionReport: InterventionReport) => {
        if (isAutoGeneratedReport(interventionReport)) {
          return t('reportDetails.autogeneratedReport');
        }
        return interventionReport.conductedBy?.firstName
          ? `${interventionReport.conductedBy.firstName} ${interventionReport.conductedBy.lastName}`
          : '-';
      },
      header: InterventionReportsTableHeader.CREATED_BY,
    },
    {
      generateCellContent: (interventionReport: InterventionReport) =>
        interventionReport.reviewedBy
          ? `${interventionReport.reviewedBy.firstName} ${interventionReport.reviewedBy.lastName}`
          : '-',
      header: InterventionReportsTableHeader.REVIEWED_BY,
    },
  ];

  return isTablet ? headers.slice(0, headers.length - 1) : headers;
};

export const getInterventionReportsKebabOptions = <
  T extends { reviewedBy?: any },
  Kebabaction extends string = string,
>(
  interventionReport: T,
  loadingMenu: { rowIndex: number; action: Kebabaction } | null,
  rowIndex: number,
): OptionWithLoading<InterventionReportsKebabOption>[] => {
  const isReviewed = Boolean(interventionReport.reviewedBy);

  const excludedOptions: InterventionReportsKebabOption[] = [];
  if (isReviewed) {
    excludedOptions.push(InterventionReportsKebabOption.REVIEW_AND_SIGN);
  }

  return Object.values(InterventionReportsKebabOption)
    .filter((option) => !excludedOptions.includes(option))
    .map((option) => ({
      label: option,
      value: camelCase(option),
      loading:
        loadingMenu?.rowIndex === rowIndex &&
        loadingMenu?.action === camelCase(option),
    }));
};

export const getInterventionReportRevieweStatusDotColor = (
  status: ReviewStatus,
) => {
  switch (status) {
    case ReviewStatus.PENDING:
      return 'bg-[#FFB103]';
    case ReviewStatus.COMPLETE:
      return 'bg-[#44D185]';
    case ReviewStatus.MISSED:
      return 'bg-[#F86060]';
    case ReviewStatus.NOT_APPLICABLE:
      return 'bg-[#D8D9E5]';
    default:
      return '';
  }
};

export const getInterventionReportsInterventionDecisionDotColor = (
  decision: InterventionDecision,
) => {
  switch (decision) {
    case InterventionDecision.REQUIRED:
      return 'bg-[#8782FA]';
    case InterventionDecision.NOT_REQUIRED:
      return 'bg-[#D8D9E5]';
    case InterventionDecision.PENDING:
      return 'bg-[#FFB103]';
    default:
      return '';
  }
};

export const renderFollowUpActionDialogContent = (followUpAction: string[]) => {
  const fields: { label: string; items: string[] }[] = [
    {
      label: 'Follow Up Actions',
      items: followUpAction,
    },
  ];

  return (
    <div className="hidden group-hover:block absolute z-[9999]">
      <Dialog>
        {fields.map(({ label, items }) => (
          <div key={label} className="mb-3">
            <strong>{label}</strong>
            {items.length > 0 ? (
              <ul className="list-disc ml-5 mt-1">
                {items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <div className="ml-5 mt-1 text-gray-500">-</div>
            )}
          </div>
        ))}
      </Dialog>
    </div>
  );
};

export const renderFollowUpActionsDialogContent = (
  interventionReport: InterventionReport,
) => {
  const followUpActions = interventionReport.followUpAction || [];

  return (
    <div className="hidden group-hover:block absolute z-[9999]">
      <Dialog>
        <div className="mb-3">
          <strong>Follow-up Actions</strong>
          {followUpActions.length > 0 ? (
            <ul className="list-disc ml-5 mt-1">
              {followUpActions.map((action, index) => (
                <li key={index}>{action}</li>
              ))}
            </ul>
          ) : (
            <div className="ml-5 mt-1 text-gray-500">-</div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export const getInterventionReportTitle = (
  reportRefNo: string | undefined,
  isReviewAndSignMode: boolean,
  t: (key: string) => string,
) => {
  return reportRefNo
    ? isReviewAndSignMode
      ? `${t('createInterventionReport.title.review')} ${reportRefNo ? `- #${reportRefNo}` : ''}`
      : `${t('createInterventionReport.title.edit')} ${reportRefNo ? `- #${reportRefNo}` : ''}`
    : `${t('createInterventionReport.title.new')}`;
};

const hasIdProperty = (obj: any): obj is { _id: string } => {
  return obj && typeof obj._id === 'string';
};

export const validateInterventionReportsForDeletion = (
  reports: InterventionReport[],
  userDetail?: GetUsersResponse,
): { valid: InterventionReport[]; invalid: string[] } => {
  const valid: InterventionReport[] = [];
  const invalid: string[] = [];

  reports.forEach((report) => {
    if (report.reviewedBy) {
      invalid.push('Reviewed reports cannot be deleted');
      return;
    }

    if (userDetail?.type === UserType.VENUE_STAFF) {
      const currentDate: Date = new Date();
      currentDate.setHours(0, 0, 0, 0);
      const reportDate: Date = new Date(report.createdAt);
      reportDate.setHours(0, 0, 0, 0);

      if (reportDate.getTime() !== currentDate.getTime()) {
        invalid.push('Only current date reports can be deleted');
        return;
      }
      if (
        !hasIdProperty(report.conductedBy) ||
        report.conductedBy._id !== userDetail.id
      ) {
        invalid.push('Staff can only delete reports they created');
        return;
      }
    }

    valid.push(report);
  });

  return { valid, invalid };
};

export const formatSelectedReportRefs = (
  reports: InterventionReport[],
): string => {
  return reports
    .map((report) => report.reportRefNo)
    .filter(Boolean)
    .join(', ');
};

/**
 * Renders a group of action buttons (Download, Delete) along with a summary of selected items.
 *
 * @param selectedCount - Number of selected items to show in the summary.
 * @param onClose - Callback triggered when the close icon is clicked.
 * @param onDelete - Callback triggered when the "Delete" button is clicked.
 * @param onDownload - Callback triggered when the "Download" button is clicked.
 *
 * @returns JSX.Element containing the action buttons and selection summary.
 */
export const renderActionButtons = (
  selectedCount: number,
  reportLabel: string,
  onClose?: () => void,
  onDelete?: () => void,
  onDownload?: () => void,
  showDownloadLoading?: boolean,
) => {
  return (
    <div className="flex items-center flex-wrap gap-2">
      {
        <ActionButton
          className="px-8 py-2 ml-2 bg-white hover:bg-white hover:text-[#1A1A1A] text-[#1A1A1A] border border-black rounded-full text-sm"
          onClick={onDownload}
        >
          {showDownloadLoading ? (
            <Loading height={20} width={20} />
          ) : (
            <Trans i18nKey="harmReports.actionButton.download" />
          )}
        </ActionButton>
      }

      {
        <ActionButton
          className="px-8 py-2 ml-2 bg-white hover:bg-white hover:text-[#E50000] text-[#E50000] border border-black rounded-full text-sm"
          onClick={onDelete}
        >
          <Trans i18nKey="harmReports.actionButton.delete" />
        </ActionButton>
      }

      <span className="flex items-center text-[12px] text-[#092344] lowercase">
        <Trans
          i18nKey="harmReports.selectedHarmReport"
          values={{
            count: selectedCount,
            report: reportLabel,
          }}
        />{' '}
        <img
          onClick={onClose}
          src={Close}
          alt="Close"
          className="w-3 h-3 cursor-pointer ml-3"
        />
      </span>
    </div>
  );
};

export const isAutoGeneratedReport = (
  report: { conductedBy?: { firstName?: string } } | null,
): boolean => report?.conductedBy?.firstName === 'System';
